import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/lac/Navbar";
import Footer from "../components/lac/Footer";

const SECTIONS = [
  {
    id: "deposit",
    title: "1. Deposits & cancellations",
    body: (
      <>
        <p>
          A non-refundable <b className="text-white">£50 deposit</b> is required to secure your
          booking date and time. By paying this deposit you confirm acceptance of these Terms.
        </p>
        <p>
          The deposit will only be refunded in full if London Automotive Customs Ltd ("LAC") is
          unable to complete your booking (e.g. workshop unavailability, equipment issue, or any
          fault on our side). In every other case the deposit is forfeit.
        </p>
        <p>
          You may reschedule your booking once, free of charge, with at least 48 hours' notice. If
          you cancel or do not attend, the deposit is forfeit and a fresh deposit will be required
          to rebook.
        </p>
      </>
    ),
  },
  {
    id: "balance",
    title: "2. Balance & payment",
    body: (
      <>
        <p>
          The remaining balance is due on the day of your install, in full, before keys are
          returned. We accept card, bank transfer, and cash. The deposit is applied against your
          final invoice.
        </p>
        <p>
          Combo discounts (e.g. 15% off when bundling Starlights + Ambient on the same booking)
          apply to the combined price of both packages booked together on one visit only and cannot
          be combined with any other offer.
        </p>
      </>
    ),
  },
  {
    id: "scope",
    title: "3. Scope of work",
    body: (
      <>
        <p>
          Quotes are based on the vehicle and package details you provide. If on inspection the
          vehicle requires additional labour or parts (e.g. unusual trim, prior modifications,
          damaged wiring), we will quote the additional cost before continuing and you are free to
          accept, decline, or reschedule.
        </p>
        <p>
          Custom Parts (digital cockpit retrofits, bespoke fabrication, etc.) are priced per build
          following a free consultation. A separate written quote will be issued before work begins.
        </p>
      </>
    ),
  },
  {
    id: "vehicle",
    title: "4. Your vehicle",
    body: (
      <>
        <p>
          You confirm you are the registered keeper of the vehicle, or have the keeper's authority
          to commission this work. Please remove personal belongings before drop-off.
        </p>
        <p>
          LAC takes every care with your vehicle but is not liable for pre-existing faults,
          mechanical or electrical, that may surface during or after the install. We carry public
          liability insurance for damage caused by our workmanship.
        </p>
      </>
    ),
  },
  {
    id: "warranty",
    title: "5. Warranty",
    body: (
      <>
        <p>
          All ambient and starlight installations carry a 12-month workmanship warranty from the
          install date. The warranty covers fitting and wiring; it excludes damage from accidents,
          tampering, third-party modifications, or unauthorised repairs.
        </p>
        <p>
          Bring the vehicle back to us within the warranty period and we will diagnose and repair
          covered issues free of charge.
        </p>
      </>
    ),
  },
  {
    id: "data",
    title: "6. Data & privacy",
    body: (
      <>
        <p>
          We collect your name, email, phone, vehicle and booking details solely to process your
          booking and communicate with you. We do not sell or share your information. Vehicle
          registration lookups are performed via the UK DVLA API only to confirm fitment.
        </p>
        <p>
          You may request deletion of your booking record at any time by contacting{" "}
          <a className="text-[#D946EF] hover:underline" href="mailto:lac.ltd24@gmail.com">
            lac.ltd24@gmail.com
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "7. Contact",
    body: (
      <>
        <p>
          London Automotive Customs Ltd · Unit 15, Rainham, RM13 8HZ ·{" "}
          <a className="text-[#D946EF] hover:underline" href="tel:+447538963090">
            07538 963090
          </a>{" "}
          ·{" "}
          <a className="text-[#D946EF] hover:underline" href="mailto:lac.ltd24@gmail.com">
            lac.ltd24@gmail.com
          </a>
        </p>
        <p className="text-xs text-zinc-500">Last updated: {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}</p>
      </>
    ),
  },
];

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 pt-32 pb-24" data-testid="terms-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition" data-testid="terms-back">
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>

        <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-3">TERMS &amp; CONDITIONS</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
          The fine print, plainly written.
        </h1>
        <p className="text-zinc-400 mt-4 font-body leading-relaxed">
          These are the terms that apply when you book an install with LAC. They're written in
          straight English so there's no ambiguity. By paying your deposit you confirm you've read
          and accepted them.
        </p>

        <div className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} data-testid={`terms-section-${s.id}`}>
              <h2 className="font-display font-bold text-xl tracking-tight text-white mb-3">
                {s.title}
              </h2>
              <div className="space-y-3 text-zinc-300 text-sm font-body leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-wrap gap-3">
          <Link
            to="/booking"
            data-testid="terms-book-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D946EF] text-white font-semibold text-sm hover:bg-[#F0ABFC] transition"
          >
            Book an install
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:border-[#D946EF]/60 transition text-sm"
          >
            Back home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
